import { useCallback, useEffect, useState } from "react";
import { Loader2, Phone, PhoneCall, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const OUTCOMES = [
  ["booked", "Booked", "bg-emerald-100 text-emerald-800"],
  ["interested", "Interested", "bg-blue-100 text-blue-800"],
  ["callback", "Callback", "bg-amber-100 text-amber-800"],
  ["no_answer", "No answer", "bg-gray-100 text-gray-600"],
  ["not_interested", "Not interested", "bg-red-100 text-red-700"],
];
const OUTCOME_STYLE = Object.fromEntries(OUTCOMES.map(([id, , cls]) => [id, cls]));
const EMPTY = { name: "", phone: "", duration_min: "", outcome: "interested", notes: "", next_followup: "" };

// Calls CRM — log every phone call against a lead, track outcomes, never lose a follow-up.
export const CallsTab = () => {
  const [calls, setCalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: list }, { data: st }] = await Promise.all([
        api.get("/admin/calls", { params: filter ? { outcome: filter } : {} }),
        api.get("/admin/calls/stats"),
      ]);
      setCalls(list.calls || []);
      setStats(st);
    } catch {
      /* quiet */
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!form.phone.trim()) {
      toast.error("Phone number is required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/calls", { ...form, duration_min: parseInt(form.duration_min || "0", 10) });
      toast.success("Call logged");
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save the call");
    } finally {
      setSaving(false);
    }
  };

  const chips = stats
    ? [["Total", stats.total], ["Today", stats.today], ["This week", stats.week], ["Booked", stats.booked], ["Follow-ups due", stats.followups_due]]
    : [];

  return (
    <div className="space-y-5" data-testid="calls-tab">
      {/* command bar */}
      <div className="glass flex flex-wrap items-center gap-x-7 gap-y-3 rounded-2xl px-5 py-3.5" data-testid="calls-command-bar">
        {chips.map(([label, val]) => (
          <div key={label} className="flex items-baseline gap-2" data-testid={`calls-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="font-serif text-xl font-semibold text-[#1A1615]">{val}</span>
            <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#91837C]">{label}</span>
          </div>
        ))}
        <div className="ml-auto">
          <button onClick={() => setShowForm((v) => !v)} data-testid="calls-add-toggle" className="btn-gold inline-flex items-center gap-2 px-4 py-2 text-[10px]">
            <Plus className="h-3.5 w-3.5" /> Log a Call
          </button>
        </div>
      </div>

      {/* add form */}
      {showForm && (
        <div className="glass rounded-2xl p-5" data-testid="calls-add-form">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <input className="input-lux text-xs" placeholder="Caller name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="calls-form-name" />
            <input className="input-lux text-xs" placeholder="Phone (+971…)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="calls-form-phone" />
            <input className="input-lux text-xs" type="number" min="0" placeholder="Duration (minutes)" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="calls-form-duration" />
            <select className="input-lux text-xs" value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} data-testid="calls-form-outcome">
              {OUTCOMES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
            <label className="text-[10px] font-medium text-[#91837C]">
              Next follow-up
              <input className="input-lux mt-1 w-full text-xs" type="date" value={form.next_followup} onChange={(e) => setForm({ ...form, next_followup: e.target.value })} data-testid="calls-form-followup" />
            </label>
            <input className="input-lux self-end text-xs" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="calls-form-notes" />
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={save} disabled={saving} data-testid="calls-form-save" className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-[10px] disabled:opacity-60">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PhoneCall className="h-3.5 w-3.5" />} Save Call
            </button>
            <button onClick={() => setShowForm(false)} className="btn-outline-lux px-4 py-2.5 text-[10px]" data-testid="calls-form-cancel">Cancel</button>
          </div>
        </div>
      )}

      {/* filter + list */}
      <div className="flex flex-wrap gap-1.5">
        {[["", "All"], ...OUTCOMES.map(([id, l]) => [id, l])].map(([id, label]) => (
          <button key={id || "all"} onClick={() => setFilter(id)} data-testid={`calls-filter-${id || "all"}`}
            className={`rounded-full px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors ${filter === id ? "bg-[#1A1615] text-[#D4AF37]" : "bg-white text-[#91837C] hover:text-[#1A1615]"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-2" data-testid="calls-list">
        {calls.map((c, i) => (
          <div key={c.id} className="glass flex flex-wrap items-center gap-3 rounded-2xl px-5 py-4" data-testid={`calls-row-${i}`}>
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F8F3E6]">
              <Phone className="h-4 w-4 text-[#B38B2D]" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#1A1615]">{c.name || "Unknown"} <span className="ml-1 text-xs font-light text-[#91837C]">{c.phone}</span></p>
              <p className="text-[11px] font-light text-[#91837C]">
                {new Date(c.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                {c.duration_min ? ` · ${c.duration_min} min` : ""}
                {c.notes ? ` · ${c.notes.slice(0, 80)}` : ""}
                {c.next_followup ? ` · follow-up ${c.next_followup}` : ""}
              </p>
            </div>
            <span className={`rounded-full px-3 py-1 text-[9px] font-bold uppercase tracking-[0.1em] ${OUTCOME_STYLE[c.outcome] || "bg-gray-100 text-gray-600"}`}>{c.outcome.replace(/_/g, " ")}</span>
            <a href={`tel:${c.phone}`} data-testid={`calls-dial-${i}`} className="btn-outline-lux inline-flex items-center gap-1.5 px-3.5 py-2 text-[10px]">
              <PhoneCall className="h-3 w-3" /> Call
            </a>
            <button
              onClick={async () => { await api.delete(`/admin/calls/${c.id}`); load(); }}
              data-testid={`calls-delete-${i}`}
              className="text-[#C9B8A8] transition-colors hover:text-red-500"
              aria-label="Delete call"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {!calls.length && (
          <p className="glass rounded-2xl p-8 text-center text-sm font-light text-[#91837C]">
            No calls logged yet — tap "Log a Call" after your next patient call to start building the record.
          </p>
        )}
      </div>
    </div>
  );
};
